/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { Redirect, Route, RouteProps, useLocation } from 'react-router';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { getStorage } from 'src/core/utils/getStorage';
import { LuisConstants } from 'src/core/utils/LuisConstants';

const sessionStorage = getStorage('sessionStorage');

/**
 * A component that disallows navigation to its given path when
 * the user is not signed in and redirects them to the home page.
 *
 * @param props The component input.
 */
export const PrivateRoute = observer((props: RouteProps) => {
    const location = useLocation();
    const { rootStore, dispatcher } = useGlobalState();
    const [componentToRender, setComponentToRender] = React.useState(<></>);

    const isAuthLoading = rootStore.authStore.isLoading;
    const { isSignedIn } = rootStore.authStore;

    React.useEffect(() => {
        const sessionUrl = sessionStorage.getData(LuisConstants.StorageKeys.SessionUrlStorageKey);
        /**
         * The user is not signed in and has accessed a protected url. Save
         * the url that the user required and automatically login.
         */
        if (!isSignedIn) {
            sessionStorage.setData(LuisConstants.StorageKeys.SessionUrlStorageKey, location.pathname);
            dispatcher.dispatch('login');

            return;
        }

        /**
         * If a session url had existed before, then redirect to that
         * session url.
         */
        if (sessionUrl) {
            sessionStorage.removeData(LuisConstants.StorageKeys.SessionUrlStorageKey);
            setComponentToRender(<Redirect to={sessionUrl} />);

            return;
        }

        setComponentToRender(<Route {...props} />);
    }, [isAuthLoading, isSignedIn, location]);

    return componentToRender;
});

PrivateRoute.displayName = 'PrivateRoute';
